import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Drawer, DrawerContent, DrawerNavigation } from '@progress/kendo-react-layout';
import { TextArea, NumericTextBox, Input, Switch, Slider } from '@progress/kendo-react-inputs';
import { DatePicker, DateTimePicker } from '@progress/kendo-react-dateinputs';
import { FaArrowLeft, FaTimesCircle, FaFileExcel, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import FormResponsesGrid from '../../components/grids/FormResponsesGrid';
import { getFormResponses, getFormById, FormResponse, updateFormQuestionResponse, FormQuestionResponseDTO } from '../../data/formData';
import { useToast } from '../../contexts/ToastContext';
import { JobNumberFormatter } from '../../data/helper';
import { getDocument, getDocumentObjectUrl } from '../../data/fileData';
import { canEditArea } from '../../helpers/areaAccess';
import '../../pages/Styles.css';

// The stored image of a Signature answer. Fetched with the auth header, so it is
// shown from an object URL rather than a plain src. Click to open full size.
const SignatureImage = ({ documentId }: { documentId: number }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    getDocumentObjectUrl(documentId)
      .then((objectUrl) => {
        if (cancelled) {
          window.URL.revokeObjectURL(objectUrl);
          return;
        }
        url = objectUrl;
        setSrc(objectUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [documentId]);

  if (failed) return <span>Signed (the image couldn't be loaded)</span>;
  if (!src) return <span>Loading signature…</span>;

  return (
    <img
      src={src}
      alt="Signature"
      title="Open full size"
      onClick={() => getDocument(documentId, 'signature.png')}
      style={{
        maxWidth: '320px',
        maxHeight: '140px',
        background: '#fff',
        border: '1px solid #edebe9',
        borderRadius: '4px',
        cursor: 'pointer',
      }}
    />
  );
};

interface FormResponsesProps {
  allowedRoutes?: string[];
}

function FormResponses(props: FormResponsesProps) {
  // Forms.Manage or an administrator may correct answers. The server enforces the same.
  const canEdit = canEditArea(props.allowedRoutes, 'Forms');
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [responsesData, setResponsesData] = useState<FormResponse[]>([]);
  const [formName, setFormName] = useState<string>('');
  const [automationUrl, setAutomationUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<FormResponse | null>(null);
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<number, string | number | boolean | Date | null>>({});
  const [saving, setSaving] = useState<boolean>(false);

  // Helper function to get the question response ID regardless of property name
  const getQuestionResponseId = (qr: any): number | undefined => {
    return qr.formQuestionResponseId ?? qr.questionResponseId ?? qr.id;
  };

  useEffect(() => {
    loadData();
  }, [formId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load form details to get the form name and automation URL
      const formDetails = await getFormById(Number(formId));
      setFormName(formDetails.name);
      setAutomationUrl(formDetails.automationUrl || '');
      
      // Load form responses
      const responses = await getFormResponses(Number(formId));
      setResponsesData(responses);
    } catch (error: any) {
      console.error('Error loading form responses:', error);
      showToast('Error loading form responses', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (response: FormResponse) => {
    console.log('Selected response:', response);
    console.log('Question responses:', response.formQuestionResponses);
    if (response.formQuestionResponses && response.formQuestionResponses.length > 0) {
      console.log('First question response:', response.formQuestionResponses[0]);
      console.log('First question response keys:', Object.keys(response.formQuestionResponses[0]));
      console.log('ID property check:', {
        formQuestionResponseId: response.formQuestionResponses[0].formQuestionResponseId,
        questionResponseId: response.formQuestionResponses[0].questionResponseId,
        id: response.formQuestionResponses[0].id
      });
    }
    setSelectedResponse(response);
    setDrawerExpanded(true);
  };

  const handleDrawerClose = () => {
    setDrawerExpanded(false);
    setSelectedResponse(null);
    setEditingQuestionId(null);
    setEditValues({});
  };

  const handleEditClick = (qr: any) => {
    const responseId = getQuestionResponseId(qr);
    console.log('handleEditClick called for question:', qr.formQuestion?.name, 'ResponseID:', responseId);
    setEditingQuestionId(responseId ?? null);
    const questionType = qr.formQuestion?.formQuestionType?.name;
    
    // Parse the response value based on type
    let value = qr.response || '';
    
    if (questionType === 'Number' && value) {
      value = parseFloat(value) || 0;
    } else if (questionType === 'Checkbox' || questionType === 'Acknowledge') {
      value = value === 'true' || value === '1' || value === 'Yes';
    } else if (questionType === 'Date' || questionType === 'Date/Time') {
      // Parse date/time values
      value = value ? new Date(value) : null;
    } else if (questionType === 'Time' && value) {
      // For time, keep as string in HH:mm format
      try {
        const date = new Date(value);
        value = date.toTimeString().substring(0, 5); // HH:mm format
      } catch {
        value = '';
      }
    } else if (questionType === 'Slider' && value) {
      value = parseFloat(value) || 0;
    }
    
    setEditValues({ ...editValues, [responseId!]: value });
  };

  const handleCancelEdit = () => {
    if (editingQuestionId !== null) {
      const updatedEditValues = { ...editValues };
      delete updatedEditValues[editingQuestionId];
      setEditValues(updatedEditValues);
    }
    setEditingQuestionId(null);
  };

  const handleSaveEdit = async (qr: any) => {
    try {
      setSaving(true);
      
      const responseId = getQuestionResponseId(qr);
      const editValue = editValues[responseId!];
      console.log('Saving question:', qr.formQuestion?.name, 'ID:', responseId, 'EditValue:', editValue);
      
      // Convert the editValue to string for API
      let responseValue: string | undefined = '';
      const questionType = qr.formQuestion?.formQuestionType?.name;
      
      if (questionType === 'Date' || questionType === 'Date/Time') {
        if (editValue instanceof Date) {
          responseValue = editValue.toISOString();
        } else if (editValue) {
          responseValue = new Date(editValue as string).toISOString();
        } else {
          responseValue = '';
        }
      } else if (questionType === 'Time') {
        // For time, convert HH:mm string to ISO string with today's date
        if (editValue) {
          const timeStr = editValue as string;
          const today = new Date();
          const [hours, minutes] = timeStr.split(':');
          today.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
          responseValue = today.toISOString();
        } else {
          responseValue = '';
        }
      } else if (questionType === 'Checkbox' || questionType === 'Acknowledge') {
        responseValue = editValue ? 'true' : 'false';
      } else if (typeof editValue === 'number') {
        responseValue = editValue.toString();
      } else {
        responseValue = (editValue as string) || '';
      }
      
      const payload: FormQuestionResponseDTO = {
        QuestionResponseId: responseId,
        FormResponseId: qr.formResponseId,
        FormQuestionId: qr.formQuestionId,
        Response: responseValue
      };

      await updateFormQuestionResponse(responseId!, payload);
      
      console.log('Update successful, updating local state with responseValue:', responseValue);
      
      // Update the local state
      if (selectedResponse) {
        const updatedResponses = selectedResponse.formQuestionResponses?.map((item: any) => 
          getQuestionResponseId(item) === responseId 
            ? { ...item, response: responseValue }
            : item
        );
        
        console.log('Updated responses:', updatedResponses);
        
        setSelectedResponse({
          ...selectedResponse,
          formQuestionResponses: updatedResponses
        });

        // Update the responses data in the grid
        const updatedResponsesData = responsesData.map(r => 
          r.formResponseId === selectedResponse.formResponseId
            ? { ...r, formQuestionResponses: updatedResponses }
            : r
        );
        setResponsesData(updatedResponsesData);
      }

      showToast('Response updated successfully', 'success');
      
      // Clear the edit state for this question
      const updatedEditValues = { ...editValues };
      delete updatedEditValues[responseId!];
      setEditValues(updatedEditValues);
      setEditingQuestionId(null);
    } catch (error: any) {
      console.error('Error updating response:', error);
      showToast('Error updating response', 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderEditControl = (qr: any) => {
    const responseId = getQuestionResponseId(qr);
    const questionType = qr.formQuestion?.formQuestionType?.name;
    const editValue = editValues[responseId!];
    console.log('Rendering edit control for:', qr.formQuestion?.name, 'Type:', questionType, 'EditValue:', editValue);

    const updateValue = (value: any) => {
      setEditValues({ ...editValues, [responseId!]: value });
    };

    switch (questionType) {
      case 'Text':
      case 'Dropdown': // For dropdown, using textarea as fallback unless we have options
        return (
          <TextArea
            value={(editValue as string) || ''}
            onChange={(e) => updateValue(e.value || '')}
            rows={3}
            style={{ width: '100%', marginBottom: '10px' }}
            disabled={saving}
          />
        );

      case 'Number': {
        // Match the precision the question was defined with, so a whole-number
        // question doesn't get edited (and saved back) as "5.00".
        const decimalPlaces = qr.formQuestion?.decimalPlaces ?? 0;
        return (
          <NumericTextBox
            value={(editValue as number) || 0}
            onChange={(e) => updateValue(e.value || 0)}
            style={{ width: '100%', marginBottom: '10px' }}
            disabled={saving}
            format={`n${decimalPlaces}`}
          />
        );
      }

      case 'Date':
        return (
          <div style={{ width: '100%', marginBottom: '10px' }}>
            <DatePicker
              value={editValue instanceof Date ? editValue : null}
              onChange={(e) => updateValue(e.value)}
              disabled={saving}
              format="dd/MM/yyyy"
            />
          </div>
        );

      case 'Time':
        return (
          <Input
            type="time"
            value={(editValue as string) || ''}
            onChange={(e) => updateValue(e.value || '')}
            style={{ width: '100%', marginBottom: '10px' }}
            disabled={saving}
          />
        );

      case 'Date/Time':
        return (
          <div style={{ width: '100%', marginBottom: '10px' }}>
            <DateTimePicker
              value={editValue instanceof Date ? editValue : null}
              onChange={(e) => updateValue(e.value)}
              disabled={saving}
              format="dd/MM/yyyy HH:mm"
            />
          </div>
        );

      case 'Checkbox':
      case 'Acknowledge':
        return (
          <div style={{ marginBottom: '10px' }}>
            <Switch
              checked={Boolean(editValue)}
              onChange={(e) => updateValue(e.value)}
              disabled={saving}
              onLabel="Yes"
              offLabel="No"
            />
          </div>
        );

      case 'Slider':
        return (
          <div style={{ marginBottom: '10px' }}>
            <div style={{ width: '100%' }}>
              <Slider
                value={(editValue as number) || 0}
                onChange={(e) => updateValue(e.value)}
                disabled={saving}
                min={0}
                max={100}
                step={1}
              />
            </div>
            <div style={{ textAlign: 'center', marginTop: '5px', color: '#605e5c' }}>
              {`Value: ${editValue}`}
            </div>
          </div>
        );

      default:
        return (
          <TextArea
            value={(editValue as string) || ''}
            onChange={(e) => updateValue(e.value || '')}
            rows={3}
            style={{ width: '100%', marginBottom: '10px' }}
            disabled={saving}
          />
        );
    }
  };

  const formatResponseValue = (qr: any) => {
    const questionType = qr.formQuestion?.formQuestionType?.name;
    const value = qr.response;
    
    console.log('Formatting response for:', qr.formQuestion?.name, 'Type:', questionType, 'Value:', value);

    if (!value || value === '') return '-';

    switch (questionType) {
      case 'Date':
        try {
          const date = new Date(value);
          return date.toLocaleDateString('en-GB');
        } catch {
          return value;
        }

      case 'Time':
        try {
          const date = new Date(value);
          return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        } catch {
          return value;
        }

      case 'Date/Time':
        try {
          const date = new Date(value);
          return date.toLocaleString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
          });
        } catch {
          return value;
        }

      case 'Checkbox':
      case 'Acknowledge':
        return value === 'true' || value === '1' || value === 'Yes' ? 'Yes' : 'No';

      default:
        return value;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const exportToExcel = () => {
    if (!responsesData || responsesData.length === 0) {
      showToast('No data to export', 'info');
      return;
    }

    // Get all unique question names from all responses
    const allQuestions = new Set<string>();
    responsesData.forEach(response => {
      response.formQuestionResponses?.forEach((qr: any) => {
        const questionName = qr.formQuestion?.name || `Question ${qr.formQuestionId}`;
        allQuestions.add(questionName);
      });
    });

    // Convert to array and sort
    const questionNames = Array.from(allQuestions).sort();

    // Create export data with one row per response
    const jobFormatter = new JobNumberFormatter();
    const exportData = responsesData.map(response => {
      const row: any = {
        'Response ID': response.formResponseId,
        'Created Date': formatDate(response.createdDate),
        'Created By': response.employeeCreatedby?.employeeName || '-',
        'Job': response.job?.ref ? jobFormatter.format(response.job.ref) : (response.jobId || '-'),
      };

      // Add each question's response as a column
      questionNames.forEach(questionName => {
        const questionResponse = response.formQuestionResponses?.find(
          (qr: any) => (qr.formQuestion?.name || `Question ${qr.formQuestionId}`) === questionName
        );
        row[questionName] = questionResponse?.response || '-';
      });

      return row;
    });

    // Create worksheet and workbook
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Form Responses');
    
    // Generate filename with form name and date
    const dateStr = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
    const fileName = `${formName.replace(/[^a-z0-9]/gi, '_')}_responses_${dateStr}.xlsx`;
    
    XLSX.writeFile(wb, fileName);
    showToast('Export successful', 'success');
  };

  return (
    <div style={{ padding: '20px' }}>
      <Drawer
        expanded={drawerExpanded}
        position="end"
        width={600}
        mode="overlay"
        animation={{ duration: 400 }}
        onOverlayClick={handleDrawerClose}
      >
        <DrawerContent>
          <DrawerNavigation>
            {selectedResponse && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>Response Details</h1>
                  <FaTimesCircle className='closeform' onClick={handleDrawerClose} />
                </div>
                <div style={{ padding: '20px' }}>
                  {/* Response Info */}
                  <div style={{ marginBottom: '30px' }}>
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ fontWeight: '600', display: 'block', marginBottom: '5px' }}>
                        Created Date:
                      </label>
                      <div>{formatDate(selectedResponse.createdDate)}</div>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ fontWeight: '600', display: 'block', marginBottom: '5px' }}>
                        Created By:
                      </label>
                      <div>{selectedResponse.employeeCreatedby?.employeeName || '-'}</div>
                    </div>
                    {selectedResponse.jobId && (
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ fontWeight: '600', display: 'block', marginBottom: '5px' }}>
                          Job:
                        </label>
                        <div>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/jobs/?id=${selectedResponse.jobId}`);
                              handleDrawerClose();
                            }}
                            style={{ color: '#0078d4', textDecoration: 'none' }}
                          >
                            {selectedResponse.job?.ref ? new JobNumberFormatter().format(selectedResponse.job.ref) : selectedResponse.jobId}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Questions and Responses */}
                  <div>
                    <h4 style={{ marginBottom: '15px', fontSize: '1.1rem', fontWeight: '600' }}>
                      Responses:
                    </h4>
                    {!canEdit && <p className='k-form-hint'>You have view-only access to form responses.</p>}
                    {selectedResponse.formQuestionResponses && selectedResponse.formQuestionResponses.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {selectedResponse.formQuestionResponses.map((qr: any, index: number) => {
                          const responseId = getQuestionResponseId(qr);
                          const isEditing = editingQuestionId === responseId;
                          console.log('Rendering question:', qr.formQuestion?.name, 'ResponseID:', responseId, 'EditingID:', editingQuestionId, 'isEditing:', isEditing);
                          
                          return (
                            <div
                              key={responseId || index}
                              style={{
                                padding: '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                border: '1px solid #edebe9'
                              }}
                            >
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginBottom: '8px'
                              }}>
                                <div style={{ fontWeight: '600', color: '#323130' }}>
                                  {qr.formQuestion?.name || `Question ${index + 1}`}
                                </div>
                                {canEdit && !isEditing && qr.formQuestion?.formQuestionType?.name !== 'Signature' && (
                                  <Button
                                    onClick={() => handleEditClick(qr)}
                                    fillMode="flat"
                                    size="small"
                                    icon="edit"
                                    title="Edit response"
                                  >
                                    <FaEdit />
                                  </Button>
                                )}
                              </div>
                              {canEdit && isEditing ? (
                                <div>
                                  {renderEditControl(qr)}
                                  <div style={{ display: 'flex', gap: '10px' }}>
                                    <Button
                                      onClick={() => handleSaveEdit(qr)}
                                      fillMode="solid"
                                      themeColor="primary"
                                      size="small"
                                      disabled={saving}
                                    >
                                      <FaSave style={{ marginRight: '5px' }} />
                                      Save
                                    </Button>
                                    <Button
                                      onClick={handleCancelEdit}
                                      fillMode="outline"
                                      size="small"
                                      disabled={saving}
                                    >
                                      <FaTimes style={{ marginRight: '5px' }} />
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ color: '#605e5c', whiteSpace: 'pre-wrap' }}>
                                  {qr.formQuestion?.formQuestionType?.name === 'Signature' && qr.documentId ? (
                                    <SignatureImage documentId={qr.documentId} />
                                  ) : (
                                    formatResponseValue(qr)
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ color: '#605e5c', fontStyle: 'italic' }}>
                        No responses available
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </DrawerNavigation>

          {/* Back Button and Export */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Button
              onClick={() => navigate('/management/forms')}
              fillMode="outline"
            >
              <FaArrowLeft style={{ marginRight: '0.5rem' }} />
              Back to Forms
            </Button>
            <Button
              onClick={exportToExcel}
              fillMode="solid"
              themeColor="primary"
              disabled={loading || responsesData.length === 0}
            >
              <FaFileExcel style={{ marginRight: '0.5rem' }} />
              Export to Excel
            </Button>
          </div>

          {/* Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              Loading...
            </div>
          ) : (
            <FormResponsesGrid 
              data={responsesData} 
              formName={formName}
              automationUrl={automationUrl}
              onRowClick={handleRowClick}
            />
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}

export default FormResponses;
